import type { PropsWithChildren, ReactNode } from "react";
import { Component } from "react";
import { Card } from "@/components/ui/Card";
import { saveCrashReport } from "@/app/crashReport";

interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<
  PropsWithChildren,
  ErrorBoundaryState
> {
  constructor(props: PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Local-only logging for MVP. Can be extended to Sentry/Firebase later.
    console.error("App crash", error, errorInfo);
    void saveCrashReport({
      occurredAt: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
      userAgent:
        typeof navigator !== "undefined" && navigator.userAgent
          ? navigator.userAgent
          : undefined
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="mx-auto max-w-xl p-4">
          <Card className="space-y-2">
            <h2 className="text-xl font-semibold">Something went wrong</h2>
            <p className="text-sm text-app-soft">
              {this.state.message ?? "Unexpected runtime error"}
            </p>
            <p className="text-xs text-app-soft">
              If this keeps happening, send a screenshot of this screen and the time it occurred.
            </p>
            <button
              type="button"
              className="rounded-xl bg-app-surface px-4 py-2 text-sm font-semibold text-app-foreground"
              onClick={async () => {
                try {
                  const payload = JSON.stringify(
                    {
                      occurredAt: new Date().toISOString(),
                      message: this.state.message
                    },
                    null,
                    2
                  );
                  await navigator.clipboard.writeText(payload);
                } catch {
                  // ignore
                }
              }}
            >
              Copy diagnostics
            </button>
            <button
              type="button"
              className="rounded-xl bg-app-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => window.location.reload()}
            >
              Reload App
            </button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
